import { Outlet, useLocation, useNavigation } from "react-router-dom";
import Sidebar from "./sidebar/Sidebar.tsx";
import styles from "./HomeLayout.module.scss";
import { clsx } from "clsx";
import { FormattedMessage } from "react-intl";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import useIsMediaLessThan from "../../../common/core-ui/useIsMediaLessThan.ts";
import { HomeOutletContext } from "./useHomeOutletContext.ts";
import { focusMainContent } from "../../../common/utils/focus.ts";
import { useIsSidebarEnabledForRoute } from "../homeRouteHandle.ts";
import DebugDialog from "../common/debug/DebugDialog.tsx";
import { HomeContextProvider } from "../HomeContextProvider.tsx";
import CandyStripe from "./CandyStripe.tsx";
import CookieCat from "./CookieCat.ts";
import { isInIframe } from "../../../common/utils/dom.ts";
import EmbeddedHeader from "./embedded-header/EmbeddedHeader.tsx";
import PaddedDotLoader from "../search/common/PaddedDotLoader.tsx";
import { getBasePathWithoutTrailingSlash } from "common/env.ts";
import { homeLayoutId } from "./layoutUtils.tsx";
import GoogleTagManagerVariables from "routes/home/layout/GoogleTagManagerVariables.tsx";
import FacultySurveyMount from "../layout/faculty-survey/FacultySurveyMount.tsx";
import useStickyWithScrollOffset from "../../../common/core-ui/useStickyWithScrollOffset.ts";
import Zenchat from "./Zenchat.tsx";

enum NavType {
  DefaultSidebar,
  EmbeddedHeader,
  None,
}

function SkipToMainButton() {
  return (
    <button
      className={clsx("vst-off-screen-button", styles.skipButton)}
      onClick={() => focusMainContent()}
    >
      <FormattedMessage defaultMessage="Skip To Main Content" />
    </button>
  );
}

function useNavType() {
  const isSidebarEnabled = useIsSidebarEnabledForRoute();

  if (isSidebarEnabled) {
    if (isInIframe()) {
      return NavType.EmbeddedHeader;
    }
    return NavType.DefaultSidebar;
  }
  return NavType.None;
}

export default function HomeLayout() {
  const [isSidebarOpenOnSmallScreens, setIsSidebarOpenOnSmallScreens] =
    useState(false);
  const sidebarContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const screenSupportsSidebarExpansion = useIsMediaLessThan(768);
  const screenSupportsContentOverflow = useIsMediaLessThan(600);

  const navType = useNavType();

  const isSidebarExpandable =
    navType === NavType.DefaultSidebar && screenSupportsSidebarExpansion;
  const isSidebarOpen =
    navType === NavType.DefaultSidebar &&
    (!isSidebarExpandable || isSidebarOpenOnSmallScreens);
  const canContentOverflow =
    navType === NavType.DefaultSidebar && screenSupportsContentOverflow;
  const navigation = useNavigation();
  const isLoading = navigation.state === "loading";
  const showLoadingPaths = [
    "/my-courses",
    "/search/terms",
    "/my-cart",
    "/search/faculty-products",
    "/faculty-products",
    "/recommendations/faculty-products",
  ];
  const showLoading =
    isLoading &&
    showLoadingPaths.reduce((show, path) => {
      if (!show) {
        return navigation?.location?.pathname.startsWith(
          `${getBasePathWithoutTrailingSlash()}${path}`
        );
      }
      return show;
    }, false);

  const [embeddedHeaderRef, embeddedHeaderClass] =
    useStickyWithScrollOffset<HTMLDivElement>({
      extraOffset: "16px",
    });

  /**
   * When the user navigates away, close the sidebar and focus the opener button.
   * Only applies if the main content goes off-screen
   */
  useEffect(() => {
    if (canContentOverflow) {
      setIsSidebarOpenOnSmallScreens(false);
      focusMainContent();
    }
  }, [location, canContentOverflow]);

  /**
   * Focus the sidebar on user open
   */
  useLayoutEffect(() => {
    if (isSidebarOpen && isSidebarExpandable) {
      sidebarContainerRef.current?.focus();
    }
  }, [isSidebarOpen, isSidebarExpandable]);

  /**
   * Tiny Screen: Close the sidebar when a non-dialog outside gets focus due to
   * accessible but offscreen content. I think this may be a good compromise
   * over trying to make the sidebar a sometimes dialog.
   */
  const onSidebarBlur = useCallback(
    (e: React.FocusEvent) => {
      if (
        !sidebarContainerRef.current?.contains(e.relatedTarget) &&
        !(
          e.relatedTarget instanceof Element &&
          e.relatedTarget.closest(
            'dialog,[role="dialog"][aria-modal="true"],[data-sidebar-portal]'
          )
        ) &&
        isSidebarOpen &&
        canContentOverflow
      ) {
        setIsSidebarOpenOnSmallScreens(false);
      }
    },
    [isSidebarOpen, canContentOverflow]
  );

  return (
    <HomeContextProvider>
      {isInIframe() && (
        <div
          style={
            // Make sure page content can always draw into the header.
            // There can be timing issues when the header is removed and re-added
            navType === NavType.None ? { display: "none" } : undefined
          }
          className={clsx(styles.headerHolder, embeddedHeaderClass)}
          ref={embeddedHeaderRef}
        >
          <SkipToMainButton />
          <EmbeddedHeader />
        </div>
      )}
      <div
        id={homeLayoutId}
        className={clsx(
          styles.layoutContainer,
          screenSupportsSidebarExpansion && styles.containerSmall,
          canContentOverflow && styles.canContentOverflow,
          navType === NavType.EmbeddedHeader && styles.hasHeader
        )}
      >
        {isSidebarOpen && (
          <div
            className={styles.sidebarHolder}
            ref={sidebarContainerRef}
            tabIndex={-1}
            onBlur={onSidebarBlur}
          >
            <SkipToMainButton />
            <Sidebar isSidebarExpandable={isSidebarExpandable} />
          </div>
        )}
        {showLoading && (
          <div className={styles.loaderContainer}>
            <PaddedDotLoader />
          </div>
        )}
        {!showLoading && (
          <Outlet
            context={
              {
                isSidebarExpandable,
                onSidebarToggle: () => {
                  setIsSidebarOpenOnSmallScreens((isOpen) => !isOpen);
                },
              } satisfies HomeOutletContext
            }
          />
        )}
        <DebugDialog />
      </div>
      <CandyStripe />
      <CookieCat />
      <GoogleTagManagerVariables />
      <FacultySurveyMount />
      <Zenchat />
    </HomeContextProvider>
  );
}
